import { Icon } from '@/components/ui/Icon';
import {
  ImportJobItem,
  useImportJobItems,
} from '@/pages/import/hooks/useImportJobs';
import {
  mdiAlertCircle,
  mdiCheckCircle,
  mdiChevronDown,
  mdiChevronUp,
  mdiLinkVariant,
  mdiSkipNext,
} from '@mdi/js';
import * as React from 'react';

interface JobItemsDetailProps {
  jobId: number;
  failedCount: number;
  skippedCount: number;
}

type TabType = 'failed' | 'skipped' | 'all';

export const JobItemsDetail: React.FC<JobItemsDetailProps> = ({
  jobId,
  failedCount,
  skippedCount,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<TabType>('failed');

  const statusFilter = activeTab === 'all' ? undefined : activeTab;

  const { data: items = [], isLoading } = useImportJobItems(
    jobId,
    statusFilter,
    isExpanded,
  );

  const hasItemsToShow = failedCount > 0 || skippedCount > 0;

  if (!hasItemsToShow) {
    return null;
  }

  const tabs: { id: TabType; label: string; count: number; icon: string }[] = [
    { id: 'failed', label: 'Failed', count: failedCount, icon: mdiAlertCircle },
    { id: 'skipped', label: 'Skipped', count: skippedCount, icon: mdiSkipNext },
  ];

  const getStatusIcon = (status: ImportJobItem['status']) => {
    switch (status) {
      case 'success':
        return (
          <Icon path={mdiCheckCircle} size={16} className='text-green-500' />
        );
      case 'failed':
        return (
          <Icon path={mdiAlertCircle} size={16} className='text-red-500' />
        );
      case 'skipped':
        return <Icon path={mdiSkipNext} size={16} className='text-grey-500' />;
      default:
        return null;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const truncateId = (id: string) => {
    if (id.length <= 30) return id;
    return `${id.substring(0, 15)}...${id.substring(id.length - 12)}`;
  };

  return (
    <div className='mt-4 border-t pt-4'>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex items-center justify-between w-full text-left text-sm font-medium text-grey-700 hover:text-grey-900'
      >
        <span>View Details ({failedCount + skippedCount} items)</span>
        <Icon
          path={isExpanded ? mdiChevronUp : mdiChevronDown}
          size={20}
          className='text-grey-500'
        />
      </button>

      {isExpanded && (
        <div className='mt-4'>
          <div className='flex space-x-2 mb-4'>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? tab.id === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-grey-200 text-grey-700'
                    : 'bg-grey-50 text-grey-500 hover:bg-grey-100'
                }`}
              >
                <Icon path={tab.icon} size={16} className='mr-1.5' />
                {tab.label}
                <span className='ml-1.5 px-1.5 py-0.5 text-xs bg-white rounded'>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className='text-center py-8 text-grey-500'>
              <span className='animate-spin inline-block h-5 w-5 border-2 border-grey-300 border-t-grey-600 rounded-full mr-2' />
              Loading items...
            </div>
          ) : items.length === 0 ? (
            <div className='text-center py-8 text-grey-500'>
              No {activeTab === 'all' ? '' : activeTab} items found
            </div>
          ) : (
            <div className='overflow-hidden rounded-lg border border-grey-200'>
              <table className='min-w-full divide-y divide-grey-200'>
                <thead className='bg-grey-50'>
                  <tr>
                    <th className='px-4 py-2 text-left text-xs font-medium text-grey-500 uppercase'>
                      Status
                    </th>
                    <th className='px-4 py-2 text-left text-xs font-medium text-grey-500 uppercase'>
                      External ID
                    </th>
                    {activeTab === 'failed' && (
                      <th className='px-4 py-2 text-left text-xs font-medium text-grey-500 uppercase'>
                        Error
                      </th>
                    )}
                    {activeTab === 'skipped' && (
                      <th className='px-4 py-2 text-left text-xs font-medium text-grey-500 uppercase'>
                        Existing Business
                      </th>
                    )}
                    <th className='px-4 py-2 text-left text-xs font-medium text-grey-500 uppercase'>
                      Processed At
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-grey-200'>
                  {items.map(item => (
                    <tr key={item.id} className='hover:bg-grey-50'>
                      <td className='px-4 py-3 whitespace-nowrap'>
                        {getStatusIcon(item.status)}
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center'>
                          <span
                            className='text-sm text-grey-900 font-mono'
                            title={item.externalId}
                          >
                            {truncateId(item.externalId)}
                          </span>
                          {item.externalId.startsWith('http') && (
                            <a
                              href={item.externalId}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='ml-2 text-grey-400 hover:text-blue-500'
                            >
                              <Icon path={mdiLinkVariant} size={14} />
                            </a>
                          )}
                        </div>
                      </td>
                      {activeTab === 'failed' && (
                        <td className='px-4 py-3'>
                          <span className='text-sm text-red-600'>
                            {item.error || 'Unknown error'}
                          </span>
                        </td>
                      )}
                      {activeTab === 'skipped' && (
                        <td className='px-4 py-3'>
                          {item.businessId ? (
                            <a
                              href={`/businesses/${item.businessId}`}
                              className='text-sm text-blue-600 hover:underline'
                            >
                              {item.businessName ||
                                `Business #${item.businessId}`}
                            </a>
                          ) : (
                            <span className='text-sm text-grey-500'>-</span>
                          )}
                        </td>
                      )}
                      <td className='px-4 py-3 whitespace-nowrap text-sm text-grey-500'>
                        {formatDate(item.processedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
